import { ReactNode, useId, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import ListPager from "../../common/ListPager";
import { StyledButtonMain } from "../../common/Buttons";
import Tags, { touchCheckboxRows } from "../Resources/Tags";
import { TOUCH_QUERY, useViewport } from "../../../utils/useViewport";
import { DirectoryList } from "./useDirectoryList";

const PRIMARY = "#9D2362";

// A row of text-link buttons (Archive / Sort / Export): 40 px tall on touch
// screens.
export const touchLinkRow = {
  [`@media ${TOUCH_QUERY}`]: { "& > button": { minHeight: 40 } },
};

const searchSx = {
  "& label.Mui-focused": {
    color: PRIMARY,
  },
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": {
      borderColor: PRIMARY,
    },
  },
};

const touchTarget = { [`@media ${TOUCH_QUERY}`]: { minHeight: 40 } };

const countOf = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

export interface DirectorySelection {
  count: number;
  actionLabel: string;
  onAction: () => void;
  onClear: () => void;
}

interface DirectoryLayoutProps<T> {
  list: DirectoryList<T>;
  // Singular: "provider".
  itemNoun: string;
  // Pinned to the top of the screen.
  banner?: ReactNode;
  searchLabel: string;
  // Under the search box, e.g. a search option.
  searchExtras?: ReactNode;
  // Above the list (admin only).
  adminActions?: ReactNode;
  // Stack spacing between the cards.
  cardSpacing: number;
  // Must set the card's key.
  renderItem: (item: T) => ReactNode;
  // A multi-select that spans pages: "N selected · <action> · Clear".
  selection?: DirectorySelection;
}

// In the narrow filters drawer the categories are one column and the count
// is left to the drawer's "Show N" button.
const CategoryFilter = ({
  list,
  inDrawer,
}: {
  list: DirectoryList<unknown>;
  inDrawer?: boolean;
}) => {
  const idPrefix = useId();
  return (
    <Box className="bg-white rounded-lg shadow-md p-4 mb-4" sx={touchCheckboxRows}>
      <div className={`grid ${inDrawer ? "grid-cols-1" : "grid-cols-2"} mb-2`}>
        {list.categories.map((category, index) => (
          <div key={category} className="flex items-center">
            <input
              type="checkbox"
              id={`${idPrefix}-${index}`}
              name={category}
              checked={list.isCategoryChecked(category)}
              onChange={(e) => list.toggleCategory(category, e.target.checked)}
            />
            <label htmlFor={`${idPrefix}-${index}`} className="text-left ml-2">
              {category}
            </label>
          </div>
        ))}
      </div>
      <hr className="border-t-2 border-gray-300 mt-4" />
      <div className="flex items-center justify-between">
        <div className="flex items-center mt-2">
          <input
            type="checkbox"
            id={`${idPrefix}-all`}
            name="Select/Unselect All"
            checked={list.allCategoriesChecked}
            onChange={(e) => list.setAllCategories(e.target.checked)}
          />
          <label htmlFor={`${idPrefix}-all`} className="text-left ml-2">
            Select/Unselect All
          </label>
        </div>
        {!inDrawer && (
          <div className="self-end">Total Showing = {list.filtered.length}</div>
        )}
      </div>
    </Box>
  );
};

// The Providers and Resources directory page: sticky case banner, filters
// (tags with counts and categories) in a column beside the list from 900 px
// and behind a "Filters (n)" drawer below it, search, admin actions, and the
// list paged 20 at a time with a pager above and below.
function DirectoryLayout<T>({
  list,
  itemNoun,
  banner,
  searchLabel,
  searchExtras,
  adminActions,
  cardSpacing,
  renderItem,
  selection,
}: DirectoryLayoutProps<T>) {
  const { isCompact, navIsDrawer, isTouch } = useViewport();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { paged } = list;

  const tags = (
    <Tags
      availableTags={list.availableTags}
      checkedTags={list.checkedTags}
      tagCounts={list.tagCounts}
      onTagChange={list.toggleTag}
    />
  );
  const searchField = (sx: object, style?: object) => (
    <TextField
      label={searchLabel}
      value={list.search}
      onChange={(e) => list.setSearch(e.target.value)}
      style={style}
      className="bg-white w-full"
      color="secondary"
      sx={{ ...searchSx, ...sx }}
    />
  );
  const pager = (
    <ListPager
      page={paged.page}
      pageCount={paged.pageCount}
      from={paged.from}
      to={paged.to}
      total={paged.total}
      onPageChange={list.goToPage}
      itemLabel={`${itemNoun}s`}
    />
  );

  const listBody = (
    <>
      {paged.total > 0 && <div ref={list.listTopRef}>{pager}</div>}
      <Stack spacing={cardSpacing}>
        {paged.total > 0 ? (
          paged.pageItems.map(renderItem)
        ) : (
          <h3 className="text-lg font-bold text-magenta">No record found</h3>
        )}
      </Stack>
      {pager}
      {selection && selection.count > 0 && (
        <Paper
          elevation={6}
          role="region"
          aria-label={`Selected ${itemNoun}s`}
          sx={{
            position: "sticky",
            // Just above the fixed feedback button in the bottom-right
            // corner (a 40 px pill 16 px from the edge, a 48 px round button
            // on touch tablets and phones), so neither covers the other.
            bottom:
              navIsDrawer && isTouch
                ? "calc(76px + env(safe-area-inset-bottom))"
                : "68px",
            zIndex: 1050,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            backgroundColor: "#FBEFF5",
            color: "#3F1229",
            borderLeft: `5px solid ${PRIMARY}`,
          }}
        >
          <Typography role="status" sx={{ fontWeight: "bold", mr: "auto" }}>
            {selection.count} selected
          </Typography>
          <StyledButtonMain
            variant="contained"
            onClick={selection.onAction}
            sx={touchTarget}
          >
            {selection.actionLabel}
          </StyledButtonMain>
          <Button
            onClick={selection.onClear}
            sx={{ color: PRIMARY, ...touchTarget }}
          >
            Clear
          </Button>
        </Paper>
      )}
    </>
  );

  return (
    <div>
      {banner && (
        <Box
          ref={list.stickyTopRef}
          sx={{ position: "sticky", top: 0, zIndex: 1100, marginBottom: "16px" }}
        >
          {banner}
        </Box>
      )}
      {isCompact ? (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: "10px" }}>
            {searchField({ flex: 1, minWidth: 0 })}
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen(true)}
              sx={{
                flexShrink: 0,
                color: PRIMARY,
                borderColor: PRIMARY,
                backgroundColor: "white",
                "&:hover": { borderColor: PRIMARY, backgroundColor: "white" },
              }}
            >
              Filters
              {list.activeFilterCount ? ` (${list.activeFilterCount})` : ""}
            </Button>
          </Stack>
          {searchExtras}
          <Stack spacing={1} sx={{ mt: 1 }}>
            {adminActions}
            {listBody}
          </Stack>
          <Drawer
            anchor="right"
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            PaperProps={{
              sx: { width: 360, maxWidth: "88vw", backgroundColor: "#f6f6f6" },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                px: 2,
                py: 1,
                backgroundColor: "white",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold", flexGrow: 1 }}>
                Filters
              </Typography>
              <Button onClick={list.clearFilters} sx={{ color: PRIMARY }}>
                Clear all
              </Button>
              <IconButton
                aria-label="Close filters"
                onClick={() => setFiltersOpen(false)}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
            <Box sx={{ p: 2, flexGrow: 1 }}>
              <CategoryFilter list={list} inDrawer />
              {tags}
            </Box>
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                p: 2,
                paddingBottom: "calc(16px + env(safe-area-inset-bottom))",
                backgroundColor: "white",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <Button
                variant="contained"
                fullWidth
                onClick={() => setFiltersOpen(false)}
                sx={{
                  minHeight: 44,
                  backgroundColor: PRIMARY,
                  "&:hover": { backgroundColor: PRIMARY },
                }}
              >
                Show {countOf(list.filtered.length, itemNoun)}
              </Button>
            </Box>
          </Drawer>
        </>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          <div className="col-start-1 col-span-1">
            <div className="min-h-[670px]">{tags}</div>
          </div>
          <div className="col-start-2 col-span-2">
            {searchField({}, { marginBottom: "10px", borderColor: PRIMARY })}
            {searchExtras}
            <CategoryFilter list={list} />
            <Stack spacing={1}>
              {adminActions}
              {listBody}
            </Stack>
          </div>
        </div>
      )}
    </div>
  );
}

export default DirectoryLayout;
